/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import { ProgressIndicator } from '@oneaudi/unified-web-components';
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  ApolloLink,
  HttpLink,
  concat,
} from '@apollo/client';
import { LocaleServiceConfig } from '@volkswagen-onehub/locale-service';
import type { DealerContextServiceV1 } from '@oneaudi/dealer-context-service';
import {
  UserVehicleData,
  UserVehicleFeatureServiceV1,
} from '@oneaudi/user-vehicle-feature-service';
import { AuthContext } from '@oneaudi/myaudi-kit-utils';
import styled from 'styled-components';
import { ContentServiceV1 } from '@oneaudi/content-service';
import { AuthorConfig } from './models/map/author-config';
import loggerStateContext from './contexts/logger-context';
import MapLayout from './components/map/MapLayout/MapLayout';
import mapContext from './contexts/map-context';
import config from './config/config.json';
import { trackAppReadyEvent, trackInitialState } from './utils/trackingUtils';
import trackingContext from './contexts/tracking-context';
import { useInitialQueryParams } from './hooks/useInitialQueryParams';
import { SearchParams } from './models/common/searchParams';
import { useHashParams } from './hooks/useHashParams';
// import { TwoClickConsentWrapper } from './components/common/TwoClickConsentModal/style';
// import { generateBaseUrl } from './utils/generateBaseUrl';
import useHasPwdVehicle from './utils/useHasPwdVehicle';
// import { useWindowSize } from './hooks/useWindowSize';
import { mapContent } from './utils/mapContent';
import { getThemeName } from '../alpha-helper';

interface Props {
  authorConfig?: AuthorConfig;
  baseUrl?: string;
  locale?: LocaleServiceConfig;
  featureAppId?: string;
  userVehicleFeatureService?: UserVehicleFeatureServiceV1;
  contentService?: ContentServiceV1;
  config?: any;
  dealerContextService?: DealerContextServiceV1;
}

const httpLink = (devMode: boolean) =>
  new HttpLink({ uri: devMode ? config.graphqlUrlDev : config.graphqlUrlProd });

const authMiddleware = (devMode: boolean, hasPwd: boolean, localeService: LocaleServiceConfig) =>
  new ApolloLink((operation, forward) => {
    const setScope = () => {
      if (hasPwd) return { scope: 'public, kdw' };
      return {};
    };
    const locale = `${localeService.language}-${localeService.countryCode.toLocaleUpperCase()}`;

    operation.setContext(({ headers = setScope() }) => ({
      headers: {
        ...headers,
        'Accept-Language': locale,
        clientid: devMode ? '' : config.graphqlProdClientId,
      },
    }));
    return forward(operation);
  });

const createClient = (authorConfig: any, hasPwd: boolean, localeService: LocaleServiceConfig) => {
  const devMode: boolean = authorConfig && authorConfig.devMode;
  return new ApolloClient({
    link: concat(authMiddleware(devMode, hasPwd, localeService), httpLink(devMode)),
    cache: new InMemoryCache(),
  });
};

export const AppMap: React.FC<Props> = ({
  baseUrl,
  locale,
  featureAppId,
  userVehicleFeatureService,
  contentService,
  config: contentConfig,
  dealerContextService,
}: Props) => {
  const { trackingService } = trackingContext.useTrackingState();
  const { logger } = loggerStateContext.useLoggerState();
  const { parseInitialParams } = useInitialQueryParams();
  const searchParams: SearchParams = parseInitialParams();
  const [hash, resetHash] = useHashParams();
  const EXTRACT_MAP_APP_ID = '#extractappid';
  const [vehicles, setVehicles] = useState<UserVehicleData[] | boolean>(false);
  const [authenticated, setIsAuthenticated] = useState<boolean>(false);
  const [hasPwd, setHasPwd] = useState(false);
  const authContext = React.useContext(AuthContext);
  const authService = authContext;
  // *isEmpty is for better QA, with this function we would be able to test both scenarios fro myAudi
  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  const isEmpty = (obj: any) => {
    if (obj && obj?.__type === 'aem-headless') return true;
    for (const prop in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, prop)) {
        return false;
      }
    }
    return true;
  };
  const [content, setContent] = React.useState(
    isEmpty(contentConfig) ? mapContent(contentService?.getContent()) : contentConfig,
  );

  React.useEffect(() => {
    const componentAdditions = {
      componentUpdate: {
        componentInfo: {
          implementer: 6,
          version: __FEATURE_APP_VERSION__,
        },
      },
    };
    trackAppReadyEvent(trackingService, componentAdditions, 'main-application');
    logger.debug(`AppReady tracked with: ${JSON.stringify(componentAdditions)}`);
    trackInitialState(
      trackingService,
      searchParams['search-geolocation'],
      searchParams.query,
      null,
    );
  }, []);
  React.useEffect(() => {
    if (hash === EXTRACT_MAP_APP_ID) {
      // eslint-disable-next-line no-alert
      alert(`App ID: ${featureAppId}`);
      resetHash('');
    }
  }, [hash]);
  if (searchParams.query === '') {
    logger.debug('SearchApp was initialized without parameters');
  } else {
    logger.debug(`SearchApp was initialized with: ${searchParams.query}`);
  }
  const selectedPartner = {
    kvpsid: searchParams.selected as string,
    tenant: searchParams.country as string,
  };
  const favoritePartner = {
    kvpsid: searchParams.favorite as string,
    tenant: '',
  };
  // const desktopBackgroundImg = `${generateBaseUrl()}/desktop_map.png`;
  // const mobileBackgroundImg = `${generateBaseUrl()}/mobile_map.png`;

  const isAuthenticated = async () => {
    const isUserAuthenticated = await authService.isAuthenticated();

    setIsAuthenticated(isUserAuthenticated);
  };

  const getUserVehicles = async () => {
    let userVehicles: UserVehicleData[] | undefined;
    if (userVehicleFeatureService) {
      userVehicles = await userVehicleFeatureService.getVehicles();
      if (userVehicles) {
        const { hasPwd: userHasPwd } = useHasPwdVehicle(userVehicles);
        setHasPwd(userHasPwd);
        setVehicles(userVehicles);
      } else {
        setHasPwd(false);
        setVehicles(true);
      }
    }
  };
  useEffect(() => {
    if (authService) {
      isAuthenticated();
      getUserVehicles();
    }
  }, [authService]);

  // const windowSize = useWindowSize();
  useEffect(() => {
    const listener = () => {
      setContent(mapContent(contentService?.getContent()));
    };
    contentService?.onContentChange(listener);
    return () => {
      contentService?.removeOnContentChange(listener);
    };
  }, [contentService, setContent]);
  const authorDefinedFilters =
    content?.filters &&
    Object.keys(content.filters).filter((key) => content?.filters[key as unknown as number]);

  if (authorDefinedFilters?.includes('sales-r8')) authorDefinedFilters.push('service-r8');

  const authorConfig: AuthorConfig = {
    intlSearchEnabled: content?.intlSearchEnabled ?? false,
    filters: authorDefinedFilters || [],
    preselectedFilters: content?.preselectedFilters || [],
    marketGoogleKey: content?.marketGoogleKey || process.env.GOOGLE_MAPS_API_KEY || '',
    initialSearchRadius: content?.initialSearchRadius ?? '10',
    initialMapZoomLevel: content?.initialMapZoomLevel ?? '',
    showAddress: content?.showAddress || false,
    groupMarkers: content?.groupMarkers || false,
    devMode: content?.devMode || false,
    distanceUnit: content?.distanceUnit || 'km',
    showPartnerSuggestions: content?.showPartnerSuggestions || false,
    ctaBarButtonConfig: content?.ctaBarButtonConfig || [],
    displayCtaBar: content?.displayCtaBar ?? false,
    showOpeningHours: content?.showOpeningHours ?? true,
    pbcVersion: content?.pbcVersion || '',
    twoClickConsentRequired: content?.twoClickConsentRequired || false,
    twoClick: {
      headline: content?.twoClick?.headline || '',
      copyText: content?.twoClick?.copyText || '',
      toggleLabel: content?.twoClick?.toggleLabel || '',
      asideToggleHeadline: content?.twoClick?.asideToggleHeadline || '',
      asideToggleLabel: content?.twoClick?.asideToggleLabel || '',
      asideToggleCopy: content?.twoClick?.asideToggleCopy || '',
      closeButton: {
        text: content?.twoClick?.closeButton?.text as string,
        redirectURL: content?.twoClick?.closeButton?.redirectURL as string,
      },
    },
    fiveStar: {
      displayFiveStarRating: content?.fiveStar?.displayFiveStarRating || false,
      legalText: content?.fiveStar?.legalText || '',
    },
    myAudiCtaConfig: contentConfig?.myAudiCtaConfig || null,
    oneHeader: contentConfig?.oneHeader || false,
    connectDealer: content?.connectDealer || contentConfig?.connectDealer || false,
    autoConnectDealer: contentConfig?.autoConnectDealer || false,
    showComments: content?.showComments || contentConfig?.showComments || false,
    headerMobileView: contentConfig?.headerMobileView || false,
  };

  return (
    <ThemeProvider theme={{ name: getThemeName('light') }}>
      <ApolloProvider client={createClient(authorConfig, hasPwd, locale!)}>
        <mapContext.Provider
          query={searchParams.query}
          isMapTriggeredByTeaser={searchParams['triggered-by-teaser']}
          baseUrl={baseUrl as string}
          authorConfig={authorConfig}
          filters={searchParams.filters}
          searchGeoLocation={searchParams['search-geolocation']}
          selectedDealerId={searchParams['dealer-id']}
          selectedPartner={selectedPartner}
          favoritePartner={favoritePartner}
          locale={locale as LocaleServiceConfig}
          dealerContextService={dealerContextService}
          connectedDealer={null}
        >
          {authenticated ? (
            vehicles ? (
              <MapLayout data-test-id="app-search-layout-container" />
            ) : (
              <LoaderContainer>
                <ProgressIndicator
                  aria-label="spinner"
                  colorScheme="idle"
                  label=""
                  size="lg"
                  type="infinite"
                  variant="spinner"
                />
              </LoaderContainer>
            )
          ) : (
            <MapLayout data-test-id="app-search-layout-container" />
          )}
        </mapContext.Provider>
        {/* </TwoClickConsentWrapper> */}
      </ApolloProvider>
    </ThemeProvider>
  );
};

export default AppMap;

export interface UnifiedTheme {
  theme: {
    sys: {
      color: {
        canvas: {
          [200]?: string;
          default?: string;
        };
      };
    };
  };
}

export const LoaderContainer = styled.div`
  align-items: center;
  background-color: ${(props: UnifiedTheme) =>
    props.theme.sys.color.canvas[200] || props.theme.sys.color.canvas.default};
  display: flex;
  height: 100%;
  justify-content: center;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  z-index: 1;
`;
